import { computed, inject, Injectable, signal } from "@angular/core";
import { Minor, MinorExamComponent } from "@app/core/domain/exam-component";
import { ProposalBlocksExamComponents, ProposalScope } from "@app/core/domain/proposal-status";
import { StatusMessageType } from "@app/core/domain/status-message";
import { Role } from "@app/core/domain/user";
import { Course } from "@core/domain/course";
import { CourseHelper } from "@core/domain/helpers/course-helper";
import { PlanHelper } from "@core/domain/helpers/plan-helper";
import { Plan } from "@core/domain/plan";
import { Period, PlanDetails } from "@core/domain/plan-details";
import { PlanLater, Specialisation } from "@core/domain/plan-later";
import { AuthService } from "@core/services/auth.service";
import { StatusMessageService } from "@feature/plan/services/status-message.service";
import { TranslateService } from "@ngx-translate/core";
import { ToastService } from "@shared/services/toast-service";
import { CourseData, SelectedPeriod } from "@shared/types/course-data";
import { MainPage } from "@shared/types/pages";
import { NgxPermissionsService } from "ngx-permissions";

@Injectable({
  providedIn: "root",
})
export class PlanStateService {
  private plansObject: { [key: number]: PlanDetails } = {};

  private readonly authService = inject(AuthService);
  private readonly planHelper = inject(PlanHelper);
  private readonly courseHelper = inject(CourseHelper);
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToastService);
  private readonly statusMessageService = inject(StatusMessageService);
  private readonly permissionsService = inject(NgxPermissionsService);

  plans = signal<Plan[] | undefined>(undefined);
  private readonly selectedPlan = signal<PlanDetails | undefined>(undefined);
  planLater = signal<PlanLater | undefined>(undefined);
  planModified = signal<boolean>(false);

  readonly pointsPlannedCalculated = computed<string>(() => {
    return this.planHelper.calculatePointsPlanned(this.selectedPlan());
  });

  readonly currentSelectedPlan = computed<PlanDetails | undefined>(() => this.selectedPlan());

  private readonly expandedYearsByPlanId = new Map<number, string[] | undefined>();
  setExpandedYears(planId: number, years: string[] | undefined): void {
    this.expandedYearsByPlanId.set(planId, years);
  }
  getExpandedYears(planId: number): string[] | undefined {
    const storedExpandedYears = this.expandedYearsByPlanId.get(planId);
    if (!storedExpandedYears) {
      return this.selectedPlan()?.studyYears.map((year) => year.yearNr.toString());
    } else {
      return storedExpandedYears;
    }
  }

  setPlanDetails(planId: number, planDetail: PlanDetails): void {
    this.plansObject[planId] = planDetail;
    if (planDetail) {
      this.selectedPlan.set(planDetail);
      this.switchEmployeeRole();
    }
    this.statusMessageService.setStatusMessages(planDetail?.statusMessages, MainPage.PLAN);
  }

  getPlanDetails(planId: number): PlanDetails | undefined {
    return this.plansObject[planId];
  }

  isPlanModified(): boolean {
    return this.planModified();
  }

  hasValidationMessages(): boolean {
    const messages = this.statusMessageService.planStatusMessages();
    if (messages?.length === 1) {
      if (messages[0].message === this.translate.instant("schedule.labelValidateSuccess")) {
        return false; // Only one message and it's a success message, so no validation messages
      }
    }
    return !!messages?.length;
  }

  hasValidationErrorsAtPlanLevel(): boolean {
    const messages = this.statusMessageService.planStatusMessages();
    return !!messages?.find((message) => message.type === StatusMessageType.ERROR);
  }

  hasElectivesValidationErrors(): boolean {
    const messages = this.statusMessageService.planStatusMessages();
    return !!messages?.find((message) => message.forElectives && message.type === StatusMessageType.ERROR);
  }

  markPlanAsModified(modified: boolean): void {
    this.planModified.set(modified);
  }

  addCourses(year: number, period: Period, courses: Course[], academicYear: number): void {
    const plan = this.selectedPlan();
    if (!courses || courses.length === 0) {
      return;
    }

    courses.forEach((course) => {
      this.addCourseToPlan(year, period, course, academicYear);
    });

    // Mark the plan as modified (period is part of plan)
    this.selectedPlan.set({ ...plan } as PlanDetails);

    this.toastService
      .presentToast(
        this.translate.instant(courses.length > 1 ? "selectCourse.coursesArePlanned" : "selectCourse.courseIsPlanned", {
          studiejaar: year,
          periode_oms: period.periodDescription,
        }),
      )
      .catch((error) => {
        console.error("Error presenting toast for adding course(s):", error);
      });
  }

  private addCourseToPlan(year: number, period: Period, course: Course, academicYear: number): void {
    course.yearNr = year;
    course.academicYear = academicYear;
    course.periodNr = period.periodNr;
    course.periodDescription = period.periodDescription;

    const newCourse = {
      ...course,
      ribbonNr: 1,
      ribbonPeriods: course.ribbonPeriods ?? [],
      courseTests: [],
    };

    // Add new course and re-sort courses: ribbon courses first, then by code
    period.courses = this.courseHelper.sortCourses([...period.courses, newCourse]);
    // re-apply course tests
    this.planHelper.refreshCourseTests(period);

    const plan = this.selectedPlan();
    if (plan && newCourse.ribbonPeriods.length > 1) {
      this.courseHelper.planRibbonCourses(plan, newCourse);
    }

    this.markPlanAsModified(true);
  }

  removeCourseFromPlan(course: Course, showToast = true): void {
    const plan = this.selectedPlan();
    plan?.studyYears.forEach((year) => {
      year.periods.forEach((period) => {
        const initialCourseCount = period.courses.length;
        period.courses = period.courses.filter((c) => !this.courseHelper.isSameCourse(c, course));
        const newCourseCount = period.courses.length;

        if (initialCourseCount !== newCourseCount) {
          this.planHelper.refreshCourseTests(period);
        }
      });
    });
    course.yearNr = 0;
    course.periodNr = 0;
    course.periodDescription = "";

    this.selectedPlan.set({ ...plan } as PlanDetails);

    this.markPlanAsModified(true);

    if (showToast) {
      // custom courses don't have a code, so in that case show only the name without a space in front
      const courseCodePrefix = course.code ? `${course.code} ` : "";
      this.toastService
        .presentToast(
          this.translate.instant("courseRemove.removed", {
            course: `${courseCodePrefix}${this.courseHelper.getCourseName(course)}`,
          }),
        )
        .catch((error) => {
          console.error("Error presenting toast for course removal:", error);
        });
    }
  }

  isPlannedCourse(course: Course): boolean {
    return !!this.selectedPlan()
      ?.studyYears.flatMap((year) => year.periods)
      .flatMap((period) => period.courses)
      .some(
        (periodCourse) => this.courseHelper.isSameCourse(periodCourse, course) && periodCourse.yearNr !== undefined,
      );
  }

  removeDraft(planId: number): void {
    if (this.plans() && this.plansObject[planId]) {
      this.plans.update((plannen) => {
        return plannen!.filter((plan) => plan.id !== planId);
      });
    }
  }

  addDraft(planId: number, name: string, draftPlan: PlanDetails): void {
    if (this.plans() && !this.plansObject[planId]) {
      const currentPlans = this.plans() ?? [];
      this.plans.set([
        ...currentPlans,
        {
          degreeProgram: draftPlan.degreeProgram,
          isDraft: true,
          yearCount: draftPlan.yearCount,
          yearNr: draftPlan.yearNr,
          id: planId,
          name: name,
        },
      ]);
    }
  }

  public isMinorToPlanLaterAllowed(): boolean {
    // adding a minor to plan later is only allowed if there is no locked proposal
    return this.authService.isRoleStudent() && !this.planHelper.planHasLockedProposal(this.selectedPlan()!);
  }

  public isRemoveMinorFromPlanLaterAllowed(minor: Minor, minorExamComponents: MinorExamComponent[]): boolean {
    // removing a minor from plan later is not allowed if the student has enrolled for the minor
    // or if the minor is part of a locked proposal for either the electives or the whole program
    const electivesAreLocked =
      this.currentSelectedPlan()!.proposalBlocksExamComponents !== ProposalBlocksExamComponents.NONE;
    const isMinorPartOfLockedProposal =
      electivesAreLocked &&
      minorExamComponents.some((minorExamComponent) =>
        minorExamComponent.courses.some((planLaterMinorCourse) => {
          if (planLaterMinorCourse.yearNr && planLaterMinorCourse.periodNr) {
            const plannedMinorCourse = this.courseHelper.findPlannedCourse(
              planLaterMinorCourse,
              this.currentSelectedPlan(),
            )!;
            // To determine if the course is placed in the minor, don't rely on examComponentCode,
            // because in the plan grid that will be the one of the electives component in the main program,
            // instead of the minor component in the minor program.
            // Rely on the name displayed in the plan grid, composed of the exam component name
            // (match to the minorExamComponentName of the minorExamComponent, because the examComponentName will be undefined)
            // and the minor name.
            const courseIsPlacedInMinor =
              plannedMinorCourse.examComponentName === minorExamComponent.minorExamComponentName &&
              plannedMinorCourse.minorName === minor.name;
            const courseIsInLockedProposal = this.planHelper.proposalStatusIsLocked(
              plannedMinorCourse.proposalStatus,
              this.currentSelectedPlan()!.proposalReviewMoment,
            );
            return courseIsPlacedInMinor && courseIsInLockedProposal;
          }
          return false;
        }),
      );
    return this.authService.isRoleStudent() && !minor.isEnrolled && !isMinorPartOfLockedProposal;
  }

  public isSpecialisationToPlanLaterAllowed(): boolean {
    // adding a specialisation to plan later is only allowed if there are no specialisations in plan later yet
    // and there is no blocked proposal for the whole program
    return (
      this.authService.isRoleStudent() &&
      !this.planLater()?.specialisation?.specialisations.length &&
      this.currentSelectedPlan()!.proposalBlocksExamComponents !== ProposalBlocksExamComponents.ALL
    );
  }

  public isRemoveSpecialisationFromPlanLaterAllowed(specialisation: Specialisation): boolean {
    // removing a specialisation from plan later is not allowed if the student has enrolled for the specialisation
    // or if there is a locked proposal for the whole study program
    return (
      this.authService.isRoleStudent() &&
      !specialisation.isEnrolled &&
      this.currentSelectedPlan()!.proposalBlocksExamComponents !== ProposalBlocksExamComponents.ALL
    );
  }

  addCourseToPeriod(courseData: CourseData, selectedPeriod: SelectedPeriod): void {
    const plan = this.selectedPlan();
    const period = plan?.studyYears
      .find((year) => year.yearNr === selectedPeriod.year.yearNr)
      ?.periods.find((yearPeriod) => yearPeriod.periodNr === selectedPeriod.period.periodNr);

    if (selectedPeriod.period.timeslots?.length) {
      courseData.timeslots = selectedPeriod.period.timeslots;
    }

    if (period) {
      this.addCourseToPlan(selectedPeriod.year.yearNr, period, courseData, selectedPeriod.year.academicYear);
      this.selectedPlan.set({ ...plan } as PlanDetails);
    }
  }

  renameDraft(planId: number, name: string): void {
    if (this.plans() && this.plansObject[planId]) {
      this.plansObject[planId].name = name;
      const renameDraft = this.plans()?.find((plan) => plan.id === planId);
      if (renameDraft && renameDraft.name !== name) {
        renameDraft.name = name;
        this.plans.set(this.plans());
      }
    }
  }

  private switchEmployeeRole(): void {
    const plan = this.selectedPlan();

    if (plan) {
      if (plan.studentNr !== "" && this.authService.isRoleEmployeeDraftPlans()) {
        this.addStudentPlanToEmployee(plan);
        this.permissionsService.loadPermissions([Role.EMPLOYEE_VIEWS_STUDENT_PLAN]);
        return;
      }

      if (plan.studentNr === "" && this.authService.isRoleEmployeeViewsStudentPlan()) {
        this.permissionsService.loadPermissions([Role.EMPLOYEE_DRAFT_PLANS]);
      }
    }
  }

  private addStudentPlanToEmployee(plan: PlanDetails): void {
    if (this.plans() && !this.plans()?.find((p) => p.id === plan.id)) {
      const currentPlans = this.plans() ?? [];
      this.plans.set([...currentPlans, { ...plan }]);
    }
  }

  /** For use with translations where the name of the proposal is a parameter. */
  getParamProposalName(): { name: string } {
    const plan = this.selectedPlan();
    return {
      name:
        plan?.proposalScope === ProposalScope.ELECTIVES
          ? `'${plan.electivesName}'`
          : this.translate.instant("submitProposal.program"),
    };
  }
}
